import { useTranslation } from "react-i18next";
import React, { memo, useCallback, useEffect, useState } from "react";
import type { EEGNavigationTimelineProps } from "./EEGNavigationTimeline";
import WaveformMinimap from "./WaveformMinimap";
import {
  isNavigationTimelineSlidingWindow,
  MACRO_OVERVIEW_COLLAPSED_STORAGE_KEY,
  MACRO_OVERVIEW_CUSTOM_RANGE_STORAGE_KEY,
  MACRO_OVERVIEW_ZOOM_STORAGE_KEY,
  NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS,
  readMacroOverviewCollapsed,
  type MacroOverviewZoomMode,
  type NavigationTimelineVisibleRange,
} from "./eegNavigationTimelineUtils";
import "./EEGNavigationTimelineStack.css";

export interface EEGNavigationTimelineStackProps extends EEGNavigationTimelineProps {
  macroZoomMode: MacroOverviewZoomMode;
  onMacroZoomModeChange: (mode: MacroOverviewZoomMode) => void;
  macroCustomRange: NavigationTimelineVisibleRange | null;
  onMacroCustomRangeChange: (range: NavigationTimelineVisibleRange) => void;
  reviewedRanges?: import("./eegReviewProgressUtils").ReviewedTimeRange[];
  videoSyncActive?: boolean;
  macroCollapsed?: boolean;
  onMacroCollapsedChange?: (collapsed: boolean) => void;
  /** CSS px matching waveform dedicated channel-label column width. */
  plotLeftPx?: number;
}

/**
 * Unified bottom navigation strip (Stratus-style Timeline Overview).
 * Macro + micro context are collapsed into a single overview with a draggable
 * red page marker band.
 */
const EEGNavigationTimelineStack: React.FC<EEGNavigationTimelineStackProps> = ({
  macroZoomMode,
  onMacroZoomModeChange,
  macroCustomRange,
  onMacroCustomRangeChange,
  reviewedRanges = [],
  videoSyncActive = false,
  macroCollapsed: macroCollapsedProp,
  onMacroCollapsedChange,
  plotLeftPx = 0,
  ...timelineProps
}) => {
  const { t } = useTranslation("features");
  const [macroCollapsedInternal, setMacroCollapsedInternal] = useState(
    readMacroOverviewCollapsed,
  );
  const macroCollapsed = macroCollapsedProp ?? macroCollapsedInternal;
  const setMacroCollapsed = useCallback(
    (collapsed: boolean) => {
      if (onMacroCollapsedChange) {
        onMacroCollapsedChange(collapsed);
      } else {
        setMacroCollapsedInternal(collapsed);
      }
      try {
        localStorage.setItem(
          MACRO_OVERVIEW_COLLAPSED_STORAGE_KEY,
          String(collapsed),
        );
      } catch {
        // ignore storage errors
      }
    },
    [onMacroCollapsedChange],
  );

  const isLongStudy = isNavigationTimelineSlidingWindow(
    timelineProps.totalDuration,
  );

  useEffect(() => {
    try {
      localStorage.setItem(MACRO_OVERVIEW_ZOOM_STORAGE_KEY, macroZoomMode);
    } catch {
      // ignore storage errors
    }
  }, [macroZoomMode]);

  useEffect(() => {
    if (!macroCustomRange) return;
    try {
      localStorage.setItem(
        MACRO_OVERVIEW_CUSTOM_RANGE_STORAGE_KEY,
        JSON.stringify(macroCustomRange),
      );
    } catch {
      // ignore storage errors
    }
  }, [macroCustomRange]);

  const handleCustomRangeChange = useCallback(
    (range: NavigationTimelineVisibleRange) => {
      onMacroCustomRangeChange(range);
      if (macroZoomMode !== "custom") {
        onMacroZoomModeChange("custom");
      }
    },
    [macroZoomMode, onMacroCustomRangeChange, onMacroZoomModeChange],
  );

  if (isLongStudy && macroCollapsed) {
    return (
      <div className="eeg-navigation-timeline-stack">
        <div className="eeg-navigation-timeline-stack__unified-wrap eeg-navigation-timeline-stack__unified-wrap--compact">
          <div className="eeg-navigation-timeline-stack__unified">
            <WaveformMinimap
              totalDuration={timelineProps.totalDuration}
              recordingStartMs={timelineProps.recordingStartMs}
              viewportStart={timelineProps.viewportStart}
              viewportEnd={timelineProps.viewportEnd}
              playheadTime={timelineProps.playheadTime}
              playheadTimeRef={timelineProps.playheadTimeRef}
              playheadPageOffsetSeconds={
                timelineProps.playheadPageOffsetSeconds
              }
              isPlaying={timelineProps.isPlaying}
              annotations={timelineProps.annotations}
              zoomMode={macroZoomMode}
              customRange={macroCustomRange}
              onZoomModeChange={onMacroZoomModeChange}
              onCustomRangeChange={handleCustomRangeChange}
              onSeek={timelineProps.onSeek}
              reviewedRanges={reviewedRanges}
              videoSyncActive={videoSyncActive}
              showHeader={false}
              showTimeTicks={false}
              compact
              workspaceTheme={timelineProps.workspaceTheme}
              plotLeftPx={plotLeftPx}
            />
          </div>
          <button
            type="button"
            className="eeg-navigation-timeline-stack__macro-collapse-btn eeg-navigation-timeline-stack__macro-collapse-btn--overlay eeg-navigation-timeline-stack__macro-collapse-btn--expand"
            onClick={() => setMacroCollapsed(false)}
            aria-expanded={false}
            aria-label={t("ui.copy.j9k0l1m2n3o4", {
              defaultValue: "Expand timeline overview",
            })}
            title={t("ui.copy.j9k0l1m2n3o4", {
              defaultValue:
                "Expand timeline overview (restores time labels if enabled)",
            })}
          >
            <span aria-hidden="true">▴</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`eeg-navigation-timeline-stack${
        isLongStudy && macroZoomMode !== "full"
          ? " eeg-navigation-timeline-stack--windowed"
          : ""
      }`}
    >
      <div className="eeg-navigation-timeline-stack__unified-wrap">
        <div className="eeg-navigation-timeline-stack__unified">
          <WaveformMinimap
            totalDuration={timelineProps.totalDuration}
            recordingStartMs={timelineProps.recordingStartMs}
            viewportStart={timelineProps.viewportStart}
            viewportEnd={timelineProps.viewportEnd}
            playheadTime={timelineProps.playheadTime}
            playheadTimeRef={timelineProps.playheadTimeRef}
            playheadPageOffsetSeconds={timelineProps.playheadPageOffsetSeconds}
            isPlaying={timelineProps.isPlaying}
            annotations={timelineProps.annotations}
            zoomMode={isLongStudy ? macroZoomMode : "full"}
            customRange={macroCustomRange}
            onZoomModeChange={onMacroZoomModeChange}
            onCustomRangeChange={handleCustomRangeChange}
            onSeek={timelineProps.onSeek}
            reviewedRanges={reviewedRanges}
            videoSyncActive={videoSyncActive}
            showHeader
            showTimeTicks={timelineProps.showTimeTicks}
            workspaceTheme={timelineProps.workspaceTheme}
            plotLeftPx={plotLeftPx}
          />
        </div>
        {isLongStudy ? (
          <button
            type="button"
            className="eeg-navigation-timeline-stack__macro-collapse-btn eeg-navigation-timeline-stack__macro-collapse-btn--overlay"
            onClick={() => setMacroCollapsed(true)}
            aria-expanded={true}
            aria-label={t("ui.copy.i8j9k0l1m2n3", {
              defaultValue: "Collapse timeline overview",
            })}
            title={t("ui.copy.i8j9k0l1m2n3", {
              defaultValue:
                "Collapse timeline overview (hides time labels)",
            })}
          >
            <span aria-hidden="true">▾</span>
          </button>
        ) : null}
      </div>
    </div>
  );
};

export { NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS };
export default memo(EEGNavigationTimelineStack);
